/**
 * NotFoundPage — a real 404 inside the app layout
 *
 * Keeps the URL (no silent redirect), names the missing path, and offers
 * onward routes. Fleet pattern established in art-of-conversation.
 */

import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { MapPin, Home, BookOpen, Wrench } from 'lucide-react';

import Card from '../components/shared/Card';

const NotFoundPage = () => {
  const location = useLocation();

  return (
    <>
      <Helmet>
        <title>Page Not Found | Finding Your Voice</title>
      </Helmet>

      <div className="max-w-xl mx-auto px-4 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Card className="p-8 text-center space-y-4">
            <MapPin className="w-10 h-10 mx-auto text-courtyard-gold-ink" aria-hidden="true" />
            <h1 className="font-display text-2xl font-bold text-courtyard-stone">
              This isn&rsquo;t part of the courtyard
            </h1>
            <p className="text-sm text-courtyard-stone/80 leading-relaxed">
              There&rsquo;s no page at{' '}
              <code className="px-1.5 py-0.5 bg-courtyard-stone/10 rounded text-courtyard-stone/80">
                {location.pathname}
              </code>
              . The gate you&rsquo;re looking for might have moved, or was never here.
            </p>
            <div className="flex flex-wrap justify-center gap-3 pt-2">
              <Link
                to="/dashboard"
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-courtyard-clay text-white text-sm rounded-lg hover:bg-courtyard-clay-dark transition-colors"
              >
                <Home className="w-4 h-4" aria-hidden="true" /> Dashboard
              </Link>
              <Link
                to="/evenings"
                className="inline-flex items-center gap-1.5 px-4 py-2 border border-courtyard-stone/20 text-courtyard-stone text-sm rounded-lg hover:bg-courtyard-stone/5 transition-colors"
              >
                <BookOpen className="w-4 h-4" aria-hidden="true" /> Evenings
              </Link>
              <Link
                to="/toolkit"
                className="inline-flex items-center gap-1.5 px-4 py-2 border border-courtyard-stone/20 text-courtyard-stone text-sm rounded-lg hover:bg-courtyard-stone/5 transition-colors"
              >
                <Wrench className="w-4 h-4" aria-hidden="true" /> Toolkit
              </Link>
            </div>
          </Card>
        </motion.div>
      </div>
    </>
  );
};

export default NotFoundPage;
